import React, { Component, ReactNode } from 'react';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component that catches errors from child components
 * and displays a user-friendly error message instead of crashing the app.
 *
 * This is particularly useful for handling:
 * - Route validation errors (invalid URL parameters)
 * - Component rendering errors
 * - Any unhandled errors in child components
 *
 * Note: Automatically recovers from Hot Module Replacement (HMR) errors
 * during development to prevent infinite error loops on code changes.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };

    // Listen for HMR updates to reset error state before the update occurs
    // This allows recovery when code changes during development
    if (import.meta.hot) {
      import.meta.hot.on('vite:beforeUpdate', () => {
        this.setState({
          hasError: false,
          error: null,
        });
      });
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);
    // Do NOT auto-recover from non-HMR errors. Persistent errors (e.g., bad props,
    // missing data, invalid state) will cause infinite render loops if we auto-reset.
    // Users must manually reset via the error UI or navigate away.
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
    // Navigate to home
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen max-w-screen-md mx-auto">
          <ErrorState
            // TODO: check when implementing i18n
            title="Oops! Algo salió mal"
            // TODO: check when implementing i18n
            description="La aplicación encontró un error inesperado. Intenta recargar la página o vuelve al inicio. Si el problema persiste, escribe al equipo de soporte."
            onAction={this.handleReset}
            // TODO: check when implementing i18n
            actionContent="Ir al inicio"
          />
        </div>
      );
    }

    return this.props.children;
  }
}
